import { XIcon } from 'lucide-react'

import {
  getNotificationIcon,
  getNotificationTypeColor,
  getNotificationTypeBgColor,
  type Notification,
} from '../services/notificationsApi'
import { formatRelativeTime } from '../utils/format'

type NotificationItemProps = {
  notification: Notification
  onClick: (notification: Notification) => void
  onDismiss: (id: string) => void
}

export function NotificationItem({ notification, onClick, onDismiss }: NotificationItemProps) {
  const Icon = getNotificationIcon(notification.type)
  const iconColor = getNotificationTypeColor(notification.type)
  const iconBg = getNotificationTypeBgColor(notification.type)

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick(notification)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onClick(notification)
      }}
      className={`group relative flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors hover:bg-gray-50 ${
        notification.is_read ? '' : 'bg-blue-50/40'
      }`}
    >
      {/* Unread dot */}
      {!notification.is_read && (
        <span className="absolute top-5 left-1.5 h-2 w-2 rounded-full bg-blue-500" />
      )}

      {/* Icon */}
      <div
        className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${iconBg}`}
      >
        <Icon className={`h-4 w-4 ${iconColor}`} />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <p
          className={`text-sm leading-snug ${notification.is_read ? 'text-gray-700' : 'font-semibold text-gray-900'}`}
        >
          {notification.title}
        </p>
        <p className="mt-0.5 line-clamp-2 text-xs text-gray-500">{notification.message}</p>
        <p className="mt-1 text-xs text-gray-400">{formatRelativeTime(notification.created_at)}</p>
      </div>

      {/* Dismiss button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onDismiss(notification.id)
        }}
        className="mt-1 flex-shrink-0 rounded p-1 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-gray-200 hover:text-gray-600"
        aria-label="Remover notificação"
      >
        <XIcon className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}
